import { LegalPage } from "@/components/lunar/LegalPage";

const INTRO = [
    "Lunar is a social app designed to help people meet others in real life — people already at the same or nearby place, socially and professionally. Lunar makes the introduction, with technology working quietly in the background. Then it gets out of the way.",
    "Lunar is operated by Somerhill Group Ltd, a company registered in England and Wales.",
    "This privacy policy explains what personal information we collect when you use Lunar, why we collect it and how we use it.",
];

const SECTIONS = [
    {
        title: "1. Information we collect",
        paragraphs: ["When you create and use a Lunar account, we may collect information including:"],
        list: [
            "your name",
            "email address and account details",
            "date of birth or age",
            "profile photographs",
            "profile information and preferences you choose to provide",
            "your selected experience, such as Social or Network",
            "Waves (including sender, recipient, timing and mutual status), connections and other interactions within Lunar",
            "messages and information exchanged through Lunar",
            "venues you select, add or check into",
            "approximate or precise location information where you give Lunar permission to access your device location",
            "device and technical information",
            "information about how you use Lunar, including app activity and interactions",
        ],
        after: "You can choose not to provide certain information, although this may prevent some features of Lunar from working.",
    },
    {
        title: "2. How we use your information",
        paragraphs: ["We use your information to:"],
        list: [
            "create and manage your Lunar account",
            "show you people and venues relevant to your preferences and location",
            "enable check-ins and other location-based features",
            "enable Waves, connections and communication between users",
            "send notifications relating to your account, Waves, connections, venues and activity",
            "help determine the ordering and relevance of people shown within Social discovery",
            "operate, maintain and improve Lunar",
            "understand how people use Lunar",
            "detect and prevent misuse, fraud or inappropriate behaviour",
            "respond to support requests",
            "comply with our legal obligations",
        ],
        after: "Where required by law, we will ask for your consent before using certain information or device features.",
    },
    {
        title: "3. Location information",
        paragraphs: [
            "Lunar uses location information to provide features based on where you are, including finding nearby venues and confirming or enabling venue check-ins.",
            "Your location is not intended to provide other users with your precise real-time coordinates. Other users may, however, be able to see the venue at which you have chosen to check in.",
            "You can control Lunar’s access to your location through your device settings.",
        ],
    },
    {
        title: "4. Profile visibility, Social Waves and privacy",
        paragraphs: [
            "Your profile and certain information about you may be visible to other Lunar users in accordance with your settings, preferences and use of the app.",
            "When you send a Social Wave, Lunar records the sender, recipient and related timing and status information in order to operate Social discovery and determine whether a Wave becomes mutual.",
            "A recipient may be told that someone there has Waved, but the identity of the person sending a Social Wave is not disclosed to the recipient through the Wave notification. The sender’s identity in relation to that Wave is revealed within Lunar only if the recipient also Waves that user and the Wave becomes mutual.",
            "Wave information may be used internally by Lunar to help determine the ordering or relevance of people shown within Social discovery, to operate safety and integrity features, and to maintain the service.",
            "These controls describe what is shown through Lunar’s Social Wave feature. They do not prevent a user from recognising or inferring another person based on profile, location, venue or other contextual information.",
            "Network and other Lunar features may use different visibility rules and should not be assumed to use private Waves unless explicitly stated within the relevant feature.",
        ],
    },
    {
        title: "5. Who we share information with",
        paragraphs: [
            "We do not sell your personal information.",
            "We may share information with trusted service providers that help us operate Lunar, including providers of:",
        ],
        list: [
            "app hosting and infrastructure",
            "authentication and account services",
            "location and mapping services",
            "notifications",
            "analytics and performance monitoring",
            "security and technical support",
        ],
        after: "These providers may process information on our behalf in accordance with their own terms and applicable data protection requirements.",
        afterList: [
            "We may also disclose information where required by law, to protect the rights or safety of Lunar users, or in connection with a sale, restructuring or transfer of the Lunar business.",
        ],
    },
    {
        title: "6. Legal basis for using your information",
        paragraphs: ["Depending on the circumstances, we process personal information because:"],
        list: [
            "it is necessary to provide the Lunar service you have requested;",
            "you have given us consent;",
            "we have a legitimate interest in operating, securing and improving Lunar; or",
            "we are required to do so by law.",
        ],
        after: "Where we rely on consent, you may withdraw that consent at any time.",
    },
    {
        title: "7. How long we keep your information",
        paragraphs: [
            "We keep personal information for as long as reasonably necessary to provide Lunar, operate your account and fulfil the purposes described in this policy.",
            "If you delete your account, we will delete or anonymise your personal information within a reasonable period, except where we need to retain certain information for legal, security, fraud-prevention or legitimate business purposes.",
        ],
    },
    {
        title: "8. Your rights",
        paragraphs: [
            "Depending on where you live, you may have rights relating to your personal information.",
            "Under UK data protection law, these may include the right to:",
        ],
        list: [
            "request access to your personal information",
            "ask us to correct inaccurate information",
            "request deletion of your information",
            "restrict or object to certain processing",
            "request transfer of certain information",
            "withdraw consent where we rely on consent",
        ],
        after: "To exercise these rights, contact us using the details below.",
        afterList: [
            "You also have the right to complain to the Information Commissioner’s Office (ICO) if you are concerned about how we handle your personal information.",
        ],
    },
    {
        title: "9. Security",
        paragraphs: [
            "We take reasonable technical and organisational measures to protect personal information from unauthorised access, loss, misuse or disclosure.",
            "However, no internet or mobile service can guarantee absolute security.",
        ],
    },
    {
        title: "10. Age restrictions",
        paragraphs: [
            "Lunar is intended for adults aged 18 and over.",
            "We do not knowingly permit anyone under 18 to create a Lunar account. If we become aware that personal information relating to someone under 18 has been collected through an account, we may remove the account and associated information.",
        ],
    },
    {
        title: "11. International processing",
        paragraphs: [
            "Some of the technology providers we use may process information outside the United Kingdom.",
            "Where required, we will use appropriate safeguards for international transfers of personal information in accordance with applicable data protection law.",
        ],
    },
    {
        title: "12. Changes to this policy",
        paragraphs: [
            "We may update this privacy policy as Lunar develops or if our legal or regulatory obligations change.",
            "Where appropriate, we will notify users of material changes.",
        ],
    },
    {
        title: "13. Contact us",
        paragraphs: ["Lunar is operated by:", "Somerhill Group Ltd", "England and Wales"],
        after: "For privacy enquiries or requests relating to your personal information, please contact: hello@somerhillgroup.com",
    },
];

export default function Privacy() {
    return (
        <LegalPage
            title="Privacy Policy"
            updated="24 August 2026"
            intro={INTRO}
            sections={SECTIONS}
            prefix="privacy"
        />
    );
}
